import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Search, Download, RefreshCw, Users, Loader2, UserPlus, AlertTriangle, CheckCircle, MoreVertical, Undo2, XCircle, ShieldAlert, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const InWIPStockReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState({
    total_bundles: 0,
    total_qty: 0,
    staff_count: 0,
    by_staff: []
  });
  
  // Filters
  const [staffFilter, setStaffFilter] = useState('all');
  const [designFilter, setDesignFilter] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [assigningBundle, setAssigningBundle] = useState(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  
  // Handover Queue state
  const [handoverQueue, setHandoverQueue] = useState([]);
  const [handoverQueueTotal, setHandoverQueueTotal] = useState({ bundles: 0, pieces: 0 });
  const [retryingQueue, setRetryingQueue] = useState(null);
  const [confirmingHandover, setConfirmingHandover] = useState(null);
  const [unassigningBundle, setUnassigningBundle] = useState(null);

  // Clearance dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearBundle, setClearBundle] = useState(null);
  const [clearReason, setClearReason] = useState('');
  const [clearRemarks, setClearRemarks] = useState('');
  const [isClearing, setIsClearing] = useState(false);

  // Staff Capacity dialog
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);
  const [capacityInfo, setCapacityInfo] = useState(null);
  const [loadingCapacity, setLoadingCapacity] = useState(false);

  const fetchCapacity = async () => {
    setLoadingCapacity(true);
    try {
      const response = await apiClient.get('/wms/dispatch/pieces-processing/capacity-diagnostic');
      setCapacityInfo(response.data);
    } catch (error) {
      toast.error('Failed to load staff capacity');
    } finally {
      setLoadingCapacity(false);
    }
  };

  const handleBulkAssign = async () => {
    try {
      setBulkAssigning(true);
      const response = await apiClient.post('/wms/dispatch/assign-all-unassigned-bundles');
      const { assigned_count, skipped_count } = response.data;
      if (assigned_count > 0) {
        toast.success(`Assigned ${assigned_count} bundles${skipped_count > 0 ? `, ${skipped_count} skipped (no capacity)` : ''}`);
      } else {
        toast.info(response.data.message || 'No bundles to assign');
      }
      fetchReport();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to bulk assign bundles');
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleConfirmHandover = async (bundleCode) => {
    try {
      setConfirmingHandover(bundleCode);
      const response = await apiClient.post(
        `/wms/dispatch/pieces-processing/supervisor-confirm-handover?bundle_code=${encodeURIComponent(bundleCode)}`
      );
      toast.success(response.data.message || `Handover confirmed for ${bundleCode}`);
      fetchReport();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to confirm handover');
    } finally {
      setConfirmingHandover(null);
    }
  };

  const handleUnassignBundle = async (bundleCode, returnToSfg = false) => {
    try {
      setUnassigningBundle(bundleCode);
      const response = await apiClient.post(
        `/wms/dispatch/pieces-processing/unassign-bundle?bundle_code=${encodeURIComponent(bundleCode)}&return_to_sfg=${returnToSfg}`
      );
      toast.success(response.data.message || `Bundle ${bundleCode} unassigned`);
      fetchReport();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to unassign bundle');
    } finally {
      setUnassigningBundle(null);
    }
  };

  const openClearDialog = (row) => {
    setClearBundle(row);
    setClearReason('');
    setClearRemarks('');
    setClearDialogOpen(true);
  };

  const handleClearBundle = async () => {
    if (!clearReason) {
      toast.error('Please select a reason');
      return;
    }
    if (clearReason === 'Other' && !clearRemarks.trim()) {
      toast.error('Please enter remarks for "Other" reason');
      return;
    }
    try {
      setIsClearing(true);
      const params = new URLSearchParams({
        bundle_code: clearBundle.bundle_no,
        reason: clearReason,
      });
      if (clearRemarks.trim()) {
        params.append('remarks', clearRemarks.trim());
      }
      const response = await apiClient.post(`/wms/dispatch/pieces-processing/clear-bundle?${params.toString()}`);
      toast.success(response.data.message || `Bundle cleared (${response.data.pieces_freed} pcs freed)`);
      setClearDialogOpen(false);
      fetchReport();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to clear bundle');
    } finally {
      setIsClearing(false);
    }
  };

  const handleAssignBundle = async (bundleCode) => {
    try {
      setAssigning(true);
      setAssigningBundle(bundleCode);
      const response = await apiClient.post(`/wms/dispatch/assign-bundle-to-staff?bundle_code=${encodeURIComponent(bundleCode)}`);
      toast.success(`Assigned ${bundleCode} to ${response.data.assignment?.assigned_to || 'next available staff'}`);
      fetchReport();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to assign bundle');
    } finally {
      setAssigning(false);
      setAssigningBundle(null);
    }
  };

  const fetchHandoverQueue = useCallback(async () => {
    try {
      const response = await apiClient.get('/wms/dispatch/handover-queue?status=queued');
      if (response.data.success) {
        setHandoverQueue(response.data.items || []);
        setHandoverQueueTotal({
          bundles: response.data.total_bundles || 0,
          pieces: response.data.total_pieces || 0
        });
      }
    } catch (error) {
      console.error('Error fetching handover queue:', error);
    }
  }, []);

  const handleRetryQueueAssignment = async (queueId) => {
    try {
      setRetryingQueue(queueId);
      const response = await apiClient.post(`/wms/dispatch/handover-queue/retry-assignment?queue_id=${queueId}`);
      if (response.data.assigned) {
        toast.success(response.data.message);
        fetchHandoverQueue();
        fetchReport();
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to retry assignment');
    } finally {
      setRetryingQueue(null);
    }
  };

  const fetchStaffList = useCallback(async () => {
    try {
      const response = await apiClient.get('/wms/dispatch/staff-list');
      if (response.data.staff) {
        setStaffList(response.data.staff);
      }
    } catch (error) {
      console.error('Error fetching staff list:', error);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (staffFilter && staffFilter !== 'all') params.append('staff_id', staffFilter);
      if (designFilter) params.append('design_name', designFilter);
      
      const response = await apiClient.get(`/wms/dispatch/in-wip-stock-report?${params.toString()}`);
      
      if (response.data.success) {
        setReportData(response.data.report || []);
        setSummary(response.data.summary || {
          total_bundles: 0,
          total_qty: 0,
          staff_count: 0,
          by_staff: []
        });
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to fetch In-WIP stock report');
    } finally {
      setLoading(false);
    }
  }, [staffFilter, designFilter]);

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  useEffect(() => {
    fetchReport();
    fetchHandoverQueue();
  }, [fetchReport, fetchHandoverQueue]);

  const handleExport = () => {
    if (reportData.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Create CSV content
    const headers = ['Bundle No', 'Design Name', 'Qty', 'Picker', 'Staff Name', 'Status', 'Assigned At'];
    const rows = reportData.map(row => [
      row.bundle_no,
      row.design_name,
      row.qty,
      row.picker_name || '',
      row.staff_name,
      row.status,
      row.assigned_at ? new Date(row.assigned_at).toLocaleString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `in_wip_stock_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Report exported successfully');
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/sfg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">In-WIP Stock Report</h1>
                <p className="text-sm text-[#E8DCC8]">Bundles with piece processing staff</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchReport}
              variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleExport}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Staff Availability Warning */}
        {!loading && summary.regular_staff_available === 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="no-staff-warning">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800">No Regular Processing Staff Selected for Today</p>
              <p className="text-sm text-amber-700">Bundles cannot be assigned until staff is selected in Dispatch Supervisor. Go to Dispatch Supervisor and save Regular Processing Staff for today.</p>
            </div>
            <Button 
              size="sm" 
              className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
              onClick={() => navigate('/wms/dispatch-supervisor')}
              data-testid="go-to-supervisor-btn"
            >
              <Users className="w-4 h-4 mr-1" />
              Open Supervisor
            </Button>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Total Bundles</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{summary.total_bundles}</p>
              </div>
              <Package className="w-10 h-10 text-orange-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Total Pieces</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{summary.total_qty}</p>
              </div>
              <Package className="w-10 h-10 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Active Staff</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{summary.staff_count}</p>
              </div>
              <Users className="w-10 h-10 text-green-500" />
            </div>
            <Button
              size="sm"
              variant="outline"
              className="mt-3 w-full text-xs border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F] hover:text-white"
              onClick={() => { setShowCapacityDialog(true); fetchCapacity(); }}
              data-testid="staff-capacity-btn"
            >
              <Users className="w-3 h-3 mr-1" />
              View Capacity
            </Button>
          </div>
          {(summary.unassigned_bundles || 0) > 0 && (
            <div className="bg-red-50 rounded-lg border border-red-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Not Assigned</p>
                  <p className="text-3xl font-bold text-red-700 mt-1">{summary.unassigned_bundles}</p>
                  <p className="text-xs text-red-500">{summary.unassigned_qty} pcs</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                  {(summary.regular_staff_available > 0 || summary.combo_staff_available > 0) && (
                    <Button 
                      size="sm" 
                      className="bg-red-600 hover:bg-red-700 text-white text-xs"
                      onClick={handleBulkAssign}
                      disabled={bulkAssigning}
                      data-testid="assign-all-btn"
                    >
                      {bulkAssigning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UserPlus className="w-3 h-3 mr-1" />}
                      Assign All
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Staff Summary */}
        {summary.by_staff && summary.by_staff.length > 0 && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-8">
            <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4">By Staff Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {summary.by_staff.map((staff, idx) => (
                <div 
                  key={idx} 
                  className="bg-[#F5F1E8] rounded-lg p-3 cursor-pointer hover:bg-[#E5DFD0] transition-colors"
                  onClick={() => {
                    const staffId = staff.staff.match(/\(([^)]+)\)/)?.[1] || 'all';
                    setStaffFilter(staffId);
                  }}
                >
                  <p className="text-xs text-[#5A5F6E] truncate" title={staff.staff}>{staff.staff}</p>
                  <p className="text-lg font-bold text-[#1A1F2E]">{staff.qty} <span className="text-xs font-normal">pcs</span></p>
                  <p className="text-xs text-[#5A5F6E]">{staff.bundles} bundles</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Handover Queue - Bundles waiting for assignment */}
        {handoverQueue.length > 0 && (
          <div className="bg-orange-50 rounded-lg border border-orange-200 p-6 mb-8" data-testid="handover-queue-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
                <div>
                  <h3 className="text-lg font-semibold text-orange-800">Handover Queue</h3>
                  <p className="text-sm text-orange-600">
                    {handoverQueueTotal.bundles} bundle(s) • {handoverQueueTotal.pieces} pieces waiting for assignment
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchHandoverQueue}
                className="border-orange-300 text-orange-700 hover:bg-orange-100"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
            <div className="space-y-3">
              {handoverQueue.map((item) => {
                const itemId = item.queue_id || item.handover_id;
                return (
                <div 
                  key={itemId} 
                  className="bg-white rounded-lg border border-orange-200 p-4"
                  data-testid={`queue-item-${itemId}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-gray-900">Slip: {item.slip_no}</span>
                        <Badge variant="outline" className="text-orange-700 border-orange-300">
                          {item.total_bundles} bundle(s)
                        </Badge>
                        <Badge variant="outline" className="text-orange-700 border-orange-300">
                          {item.total_pieces} pcs
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">{item.design_name || item.sku_code}</p>
                      <p className="text-xs text-gray-500">
                        Queued: {new Date(item.queued_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        {item.picker_name && <span className="ml-2 text-gray-700 font-medium">• Picker: {item.picker_name}</span>}
                        {!item.picker_name && item.picker_staff_id && <span className="ml-2 text-gray-700 font-medium">• Picker: {item.picker_staff_id}</span>}
                        {item.reason && <span className="ml-2 text-orange-600">• {item.reason.replace('_', ' ')}</span>}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {item.bundles?.slice(0, 5).map((bundle, idx) => (
                          <span key={idx} className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">
                            {bundle.bundle_code}
                          </span>
                        ))}
                        {item.bundles?.length > 5 && (
                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">
                            +{item.bundles.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleRetryQueueAssignment(itemId)}
                      disabled={retryingQueue === itemId}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid={`retry-btn-${itemId}`}
                    >
                      {retryingQueue === itemId ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Retrying...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Retry Assignment
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Staff</Label>
              <Select value={staffFilter} onValueChange={setStaffFilter}>
                <SelectTrigger data-testid="staff-filter">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.staff_id} value={staff.staff_id}>
                      {staff.name} ({staff.staff_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Design Name</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={designFilter}
                  onChange={(e) => setDesignFilter(e.target.value)}
                  placeholder="Search design..."
                  className="pl-10"
                  data-testid="design-filter"
                />
              </div>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setStaffFilter('all');
                  setDesignFilter('');
                }}
                className="w-full"
                data-testid="clear-filters-btn"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
              <span className="ml-3 text-[#5A5F6E]">Loading report...</span>
            </div>
          ) : reportData.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg text-[#5A5F6E]">No In-WIP bundles found</p>
              <p className="text-sm text-gray-400">All bundles have been processed or no bundles are currently assigned</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="wip-report-table">
                <thead className="bg-[#F5F1E8]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Bundle No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Design Name</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Qty</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Picker</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff Name (ID)</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5DFD0]">
                  {reportData.map((row, idx) => (
                    <tr key={idx} className={row.is_unassigned ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-[#FFFBF5]'}>
                      <td className="px-4 py-3 text-sm font-medium text-[#1A1F2E]">{row.bundle_no}</td>
                      <td className="px-4 py-3 text-sm text-[#5A5F6E]">{row.design_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-center font-semibold text-[#1A1F2E]">{row.qty}</td>
                      <td className="px-4 py-3 text-sm text-[#5A5F6E]" data-testid={`picker-name-${row.bundle_no}`}>{row.picker_name || '-'}</td>
                      <td className={`px-4 py-3 text-sm ${row.is_unassigned ? 'text-red-600 font-semibold' : 'text-[#5A5F6E]'}`}>
                        {row.staff_name}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          row.status === 'picked_not_assigned' ? 'bg-red-100 text-red-700' :
                          row.status === 'completed' ? 'bg-purple-100 text-purple-700' :
                          row.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          row.status === 'handed_over' ? 'bg-green-100 text-green-700' :
                          row.status === 'pending_handover' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {row.status === 'picked_not_assigned' ? 'Picked - Not Assigned' :
                           row.status === 'completed' ? 'Processed' :
                           row.status === 'in_progress' ? 'In Progress' :
                           row.status === 'handed_over' ? 'Handed Over' :
                           row.status === 'pending_handover' ? 'Pending Handover' :
                           'Assigned'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.is_unassigned ? (
                          <Button
                            size="sm"
                            className="bg-[#2D5F4F] hover:bg-[#234A3E] text-white"
                            disabled={assigning && assigningBundle === row.bundle_no}
                            onClick={() => handleAssignBundle(row.bundle_no)}
                            data-testid={`assign-wip-btn-${row.bundle_no}`}
                          >
                            {assigning && assigningBundle === row.bundle_no ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <UserPlus className="w-3 h-3 mr-1" />
                            )}
                            Assign
                          </Button>
                        ) : row.status === 'put_away_complete' ? (
                          <span className="text-xs text-green-600 font-medium">Put Away Complete</span>
                        ) : row.status === 'completed' ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-xs text-gray-400 mr-1">Awaiting Put Away</span>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="px-2"
                                  data-testid={`actions-menu-${row.bundle_no}`}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => handleUnassignBundle(row.bundle_no, true)}
                                  className="text-blue-600"
                                  disabled={unassigningBundle === row.bundle_no}
                                  data-testid={`return-sfg-btn-${row.bundle_no}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Return to SFG
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => openClearDialog(row)}
                                  className="text-amber-600"
                                  data-testid={`clear-btn-${row.bundle_no}`}
                                >
                                  <ShieldAlert className="w-4 h-4 mr-2" />
                                  Clear with Reason
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : !row.is_unassigned ? (
                          <div className="flex items-center justify-center gap-1">
                            {(row.status === 'assigned' || row.status === 'pending_handover') && (
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                disabled={confirmingHandover === row.bundle_no}
                                onClick={() => handleConfirmHandover(row.bundle_no)}
                                data-testid={`confirm-handover-btn-${row.bundle_no}`}
                              >
                                {confirmingHandover === row.bundle_no ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                )}
                                Confirm Handover
                              </Button>
                            )}
                            {unassigningBundle === row.bundle_no ? (
                              <Button size="sm" variant="outline" disabled>
                                <Loader2 className="w-3 h-3 animate-spin" />
                              </Button>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="px-2"
                                    data-testid={`actions-menu-${row.bundle_no}`}
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => openClearDialog(row)}
                                    className="text-amber-600"
                                    data-testid={`clear-btn-${row.bundle_no}`}
                                  >
                                    <ShieldAlert className="w-4 h-4 mr-2" />
                                    Clear with Reason
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleUnassignBundle(row.bundle_no, false)}
                                    className="text-orange-600"
                                    data-testid={`unassign-btn-${row.bundle_no}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Unassign
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleUnassignBundle(row.bundle_no, true)}
                                    className="text-red-600"
                                    data-testid={`return-sfg-btn-${row.bundle_no}`}
                                  >
                                    <Undo2 className="w-4 h-4 mr-2" />
                                    Return to SFG Storage
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Clear Bundle Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="clear-bundle-dialog">
          <DialogHeader>
            <DialogTitle>Clear Bundle from Capacity</DialogTitle>
            <DialogDescription>
              This will remove the bundle from the staff's pending capacity. The bundle will no longer count toward their workload limit.
            </DialogDescription>
          </DialogHeader>
          {clearBundle && (
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-sm"><span className="font-semibold">Bundle:</span> {clearBundle.bundle_no}</p>
                <p className="text-sm"><span className="font-semibold">Staff:</span> {clearBundle.staff_name}</p>
                <p className="text-sm"><span className="font-semibold">Pieces:</span> {clearBundle.qty}</p>
              </div>

              <div className="space-y-2">
                <Label>Reason *</Label>
                <Select value={clearReason} onValueChange={(val) => { setClearReason(val); if (val !== 'Other') setClearRemarks(''); }}>
                  <SelectTrigger data-testid="clear-reason-select">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Damaged">Damaged</SelectItem>
                    <SelectItem value="No Color Assortment">No Color Assortment</SelectItem>
                    <SelectItem value="Wrong Design">Wrong Design</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {clearReason === 'Other' && (
                <div className="space-y-2">
                  <Label>Remarks *</Label>
                  <Textarea
                    value={clearRemarks}
                    onChange={(e) => setClearRemarks(e.target.value)}
                    placeholder="Enter remarks..."
                    className="resize-none"
                    rows={3}
                    data-testid="clear-remarks-input"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setClearDialogOpen(false)} data-testid="clear-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleClearBundle}
              disabled={isClearing || !clearReason || (clearReason === 'Other' && !clearRemarks.trim())}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="clear-confirm-btn"
            >
              {isClearing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
              {isClearing ? 'Clearing...' : 'Confirm Clear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Capacity Dialog */}
      <Dialog open={showCapacityDialog} onOpenChange={setShowCapacityDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Staff Capacity
            </DialogTitle>
            <DialogDescription>
              Live capacity of piece processing staff. Capacity is released after label printing.
            </DialogDescription>
          </DialogHeader>

          {loadingCapacity ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading...
            </div>
          ) : capacityInfo ? (
            <div className="space-y-4">
              {/* Status Banner */}
              <div className={`p-3 rounded-lg border ${
                capacityInfo.status === 'OK' 
                  ? 'bg-green-50 border-green-300' 
                  : capacityInfo.status === 'NO_STAFF_AVAILABLE'
                  ? 'bg-red-50 border-red-300'
                  : 'bg-amber-50 border-amber-300'
              }`}>
                <p className={`font-medium text-sm ${
                  capacityInfo.status === 'OK' ? 'text-green-700' : 
                  capacityInfo.status === 'NO_STAFF_AVAILABLE' ? 'text-red-700' : 'text-amber-700'
                }`} data-testid="capacity-status">
                  {capacityInfo.status === 'OK' ? 'Capacity Available' : 
                   capacityInfo.status === 'NO_STAFF_AVAILABLE' ? 'No Staff Available' : 'Full Capacity'}
                </p>
                {capacityInfo.recommendation && (
                  <p className="text-xs text-gray-500 mt-1">{capacityInfo.recommendation}</p>
                )}
              </div>

              {/* Summary Row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#F5F1E8] p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-[#1A1F2E]">{capacityInfo.total_staff || 0}</p>
                  <p className="text-xs text-[#5A5F6E]">Staff</p>
                </div>
                <div className="bg-[#F5F1E8] p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{capacityInfo.total_available_capacity || 0}</p>
                  <p className="text-xs text-[#5A5F6E]">Available</p>
                </div>
                <div className="bg-[#F5F1E8] p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{capacityInfo.queued_slips_count || 0}</p>
                  <p className="text-xs text-[#5A5F6E]">Queued</p>
                </div>
              </div>

              {/* Staff List */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-[#5A5F6E]">
                  Staff Details (Max: {capacityInfo.max_pieces_per_staff || '-'} pcs each)
                </p>
                {capacityInfo.staff?.map((s) => (
                  <div key={s.staff_id} className={`p-3 rounded-lg border ${
                    s.is_full ? 'bg-red-50 border-red-200' : 'bg-white border-[#E5DFD0]'
                  }`} data-testid={`capacity-staff-${s.staff_id}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm text-[#1A1F2E]">{s.staff_name || s.staff_id}</p>
                        <p className="text-xs text-[#5A5F6E]">{s.staff_id}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${s.is_full ? 'text-red-600' : 'text-green-600'}`}>
                          {s.available_capacity} free
                        </p>
                        <p className="text-xs text-[#5A5F6E]">{s.current_pieces}/{s.max_pieces} scanned</p>
                      </div>
                    </div>
                    
                    {/* Pending Handover Bundles */}
                    {s.pending_handover_bundles?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs font-medium text-blue-700 mb-1">
                          Pending Handovers ({s.pending_handovers})
                        </p>
                        <div className="max-h-24 overflow-y-auto space-y-1">
                          {s.pending_handover_bundles.map((b, i) => (
                            <div key={i} className="flex justify-between items-center text-xs bg-blue-50 rounded px-2 py-1">
                              <span className="font-mono font-medium text-blue-800">{b.bundle_code}</span>
                              <span className="text-blue-600">{b.piece_count} pcs <span className="text-blue-400">({b.status})</span></span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Stuck Bundles */}
                    {s.stuck_bundles?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs font-medium text-amber-700 mb-1">
                          Stuck Bundles ({s.stuck_count}) — {'>'} 4 hours since scan
                        </p>
                        <div className="max-h-24 overflow-y-auto space-y-1">
                          {s.stuck_bundles.map((b, i) => (
                            <div key={i} className="flex justify-between items-center text-xs bg-amber-50 rounded px-2 py-1">
                              <span className="font-mono font-medium text-amber-800">{b.bundle_code}</span>
                              <span className="text-amber-600">{b.usn_count} pcs <span className="text-amber-400">({b.hours_since_scan}h ago)</span></span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Queued Slips */}
              {capacityInfo.queued_slips?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#5A5F6E]">Queued Slips</p>
                  {capacityInfo.queued_slips.map((q, idx) => (
                    <div key={idx} className="bg-[#F5F1E8] p-2 rounded text-xs flex justify-between">
                      <span className="font-medium">{q.slip_no}</span>
                      <span>{q.bundle_count} bundles • {q.total_pieces} pcs</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchCapacity}
              disabled={loadingCapacity}
              data-testid="refresh-capacity-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loadingCapacity ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => setShowCapacityDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InWIPStockReport;
